/**
 * @file TopBar.jsx
 * Barra superiore dell'applicazione.
 *
 * Contiene:
 * - Pulsante toggle sidebar (hamburger)
 * - Nome utente con popover logout (click fuori per chiudere)
 * - Dropdown ruolo (sola lettura, mostra il ruolo corrente)
 * - Pulsante "Ricarica" per ricaricare tutti i dati dal BE
 *
 * NOTA — Toggle tema chiaro/scuro:
 *   Il pulsante di cambio tema è stato commentato e rimosso dall'interfaccia.
 *   L'app opera esclusivamente in light mode.
 *   Il codice del toggle (useTheme / toggleTheme) rimane importato ma non esposto
 *   nell'UI. Per riabilitare il cambio tema, ripristinare il blocco commentato
 *   nel JSX e verificare ThemeContext.jsx e globals.css (sezione .dark).
 */

import { useState, useRef, useEffect } from 'react';
import { useApp } from '../../hooks/useApp';
import { useData } from '../../hooks/useData';
import { useTheme } from '../../hooks/useTheme';
import { usePermissions } from '../../hooks/usePermissions';
import { ROLES } from '../../lib/constants/roles';
import RefreshButton from '../shared/RefreshButton';

export default function TopBar() {
  // Recupera azureEnabled dal context per mostrare/nascondere il popover utente (login/logout)
  const { userRole, userRoles, userName, userEmail, azureEnabled, setAuth, toggleSidebar, logout } = useApp();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!popoverOpen) return;
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen]);
  const { loadAll } = useData();
  const { theme, toggleTheme } = useTheme();
  const { refreshPermissions } = usePermissions();
  const role = userRole ?? ROLES.PREDEFINITO;
  const roles = userRoles;

  return (
    <header className="bg-card border-b border-line flex items-center justify-between px-4 md:px-6 py-3.5 shrink-0 shadow-sm z-10">

      {/* Sinistra: toggle sidebar + titolo */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-inner transition-colors shrink-0"
          title="Mostra/Nascondi Menu Laterale"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 md:w-6 md:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        <h1 className="text-base md:text-[1.15rem] font-semibold text-ink tracking-tight m-0 truncate">
          Pianificazione Mensile Risorse
        </h1>
      </div>

      {/* Destra: ruolo, tema, ricarica */}
      <div className="flex items-center gap-2 md:gap-4 shrink-0">
        {/* Nome Utente con popover logout — mostrato solo se Azure è abilitato */}
        {azureEnabled && (
          <div className="hidden sm:flex items-center gap-2 relative" ref={popoverRef}>
            <span style={{ filter: 'grayscale(100%) opacity(0.7)' }}>👤</span>
            <button
              onClick={() => setPopoverOpen((v) => !v)}
              className="text-sm text-muted whitespace-nowrap hover:text-ink transition-colors cursor-pointer bg-transparent border-0 p-0"
            >
              {userName}
            </button>
            {popoverOpen && (
              <div className="absolute top-full right-0 mt-2 w-52 bg-card border border-line rounded-lg shadow-lg z-50 p-3 flex flex-col gap-2">
                {userEmail && (
                  <p className="text-xs text-muted truncate border-b border-line pb-2">{userEmail}</p>
                )}
                <button
                  onClick={() => { setPopoverOpen(false); logout(); }}
                  className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 hover:bg-card-inner rounded-md px-2 py-1.5 transition-colors w-full text-left"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
        {/* Dropdown Ruolo */}
        {roles.length > 1 && (
          <div className="hidden sm:flex items-center gap-2">
            <label className="text-sm text-muted whitespace-nowrap">Ruolo</label>
            <select
              value={role}
              onChange={(e) => {
                const newRole = e.target.value;
              setAuth(newRole, null);
              // Ri-carica i permessi dal backend per il nuovo ruolo.
              // Il ruolo viene passato esplicitamente per non aspettare
              // il re-render del context (che aggiornerebbe userRole).
              refreshPermissions(newRole);
            }}
            style={{ backgroundColor: 'var(--card)', color: 'var(--ink)', borderColor: 'var(--line)' }}
            className="text-sm px-2 py-1.5 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm cursor-pointer border"
          >
            {[...roles].sort((a, b) => {
              const order = Object.values(ROLES);
              return order.indexOf(a) - order.indexOf(b);
            }).map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>)}

        {/* Toggle Tema */}
        {/* Attualmente è in disuso, lascio la logica per in futuro reimplementare la funzionalità */}
        {/* <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Passa a Light mode' : 'Passa a Dark mode'}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-line bg-card-inner text-ink hover:bg-card text-xs font-semibold transition-colors shadow-sm"
        >
          {theme === 'dark' ? (
            <>
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
              <span className="hidden md:inline">Light</span>
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
              <span className="hidden md:inline">Dark</span>
            </>
          )}
        </button> */}

        {/* Pulsante Ricarica */}
        <RefreshButton onClick={loadAll} label="Ricarica" size="sm" />
      </div>
    </header>
  );
}
